/**
 * Responsive `src`/`srcSet` for content images. Pure (no `bun:sqlite`, no
 * `node:*`), because it runs in the client bundle and in SSR and both must
 * produce the same markup.
 *
 * Three kinds of image URL:
 * - App uploads (`/uploads/<folder>/<id>/full.webp`) already have resized
 *   siblings written by `resizeAndSave` (src/api/upload.ts).
 * - Every other public image under `/uploads/` (WordPress import in
 *   `/uploads/migrated/...`, ffmpeg posters) is a single original. Those get
 *   candidates from the resize route `/img/<width>/<path under uploads>`
 *   (src/api/image-resize.ts), which makes a webp on first request and caches
 *   it on disk. No database row changes.
 * - External URLs are left alone.
 */

/** Widths the resize route accepts. Anything else is a 404. */
export const RESIZE_WIDTHS = [160, 320, 480, 640, 960, 1280, 1920] as const;
export type ResizeWidth = (typeof RESIZE_WIDTHS)[number];

export const RESIZE_ROUTE_PREFIX = "/img/";

/**
 * Upload folders whose images are public and may be resized. Private folders
 * (`documents`, `therapist-certs`) are deliberately absent: the resize route
 * serves without a session check. The route and this helper share the list,
 * so the helper never emits a candidate the route would refuse.
 */
export const RESIZABLE_FOLDERS: ReadonlySet<string> = new Set([
  "migrated",
  "profiles",
  "blog",
  "pages",
  "services",
  "service-images",
  "therapist-photos",
  "homepage-topics",
  "video-posters",
  "videos",
]);

/** Still raster formats only: an animated GIF would lose its animation. */
const RESIZABLE_EXT_RE = /\.(jpe?g|png|webp)$/i;

const SAFE_SEGMENT_RE = /^[A-Za-z0-9._-]+$/;

/**
 * True for a path relative to the upload root (`migrated/profiles/x.jpg`)
 * that the resize route may serve: allow-listed top folder, at least one more
 * segment, plain segments only (no `.`/`..`, no encoded characters), raster
 * image extension.
 */
export function isResizableUploadPath(relPath: string): boolean {
  const parts = relPath.split("/");
  if (parts.length < 2) return false;
  if (!RESIZABLE_FOLDERS.has(parts[0]!)) return false;
  for (const part of parts) {
    if (!SAFE_SEGMENT_RE.test(part) || part === "." || part === "..") return false;
  }
  return RESIZABLE_EXT_RE.test(parts[parts.length - 1]!);
}

export function resizedImageUrl(relPath: string, width: ResizeWidth): string {
  return `${RESIZE_ROUTE_PREFIX}${width}/${relPath}`;
}

const APP_VARIANT_RE = /^(\/uploads\/[^/]+\/[^/]+\/)(?:full|medium|small|xsmall)\.webp$/;

export interface ResponsiveImage {
  src: string;
  srcSet?: string;
}

export function responsiveImage(url: string): ResponsiveImage {
  const variant = APP_VARIANT_RE.exec(url);
  if (variant) {
    const base = variant[1];
    return {
      src: url,
      srcSet: [
        `${base}xsmall.webp 150w`,
        `${base}small.webp 400w`,
        `${base}medium.webp 800w`,
        `${base}full.webp 1600w`,
      ].join(", "),
    };
  }

  if (url.startsWith("/uploads/")) {
    const rel = url.slice("/uploads/".length);
    if (isResizableUploadPath(rel)) {
      return {
        src: url,
        srcSet: RESIZE_WIDTHS.map((w) => `${resizedImageUrl(rel, w)} ${w}w`).join(", "),
      };
    }
  }

  return { src: url };
}
